import {
  Panel,
  StatBar,
  StatRow,
  StatTile,
  formatDecimal,
  formatMoney,
  formatNumber,
  formatPercent,
} from "./statsUi"

// The board-level record: what a player builds, bids on, trades for and rolls.
// Everything here is a lifetime count, with the per-game figure alongside it so
// a long career and a short one can still be read the same way.
const BoardTab: React.FC<{ stats: IUserLifetimeStatsResponse }> = ({
  stats,
}) => {
  const games = stats.gamesPlayed || 0
  const perGame = (total: number) => (games > 0 ? total / games : 0)
  const tradesResolved =
    (stats.tradesAccepted || 0) + (stats.tradesDeclined || 0)

  return (
    <div className="flex flex-col gap-4 sm:gap-2.5">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-2">
        <StatTile
          label="Properties"
          value={formatNumber(stats.propertiesBought)}
          sub={`${formatDecimal(stats.derived.averagePropertiesPerGame)} per game`}
          icon="fa-building"
          accent="#FBBF24"
        />
        <StatTile
          label="Houses Built"
          value={formatNumber(stats.housesBuilt)}
          sub={`${formatNumber(stats.housesDestroyed)} sold or lost`}
          icon="fa-hammer"
          accent="#34D399"
        />
        <StatTile
          label="Auctions Won"
          value={formatNumber(stats.auctionsWon)}
          sub={`${formatPercent(stats.derived.auctionWinRate, 0)} of auctions bid`}
          icon="fa-gavel"
          accent="#A78BFA"
        />
        <StatTile
          label="Trades Done"
          value={formatNumber(stats.tradesAccepted)}
          sub={`${formatPercent(stats.derived.tradeAcceptRate, 0)} accepted`}
          icon="fa-right-left"
          accent="#38BDF8"
        />
        <StatTile
          label="Cards Played"
          value={formatNumber(stats.cardsPlayed)}
          sub={`${formatNumber(stats.cardsDrawn)} drawn`}
          icon="fa-clone"
          accent="#FB923C"
        />
        <StatTile
          label="Times Jailed"
          value={formatNumber(stats.timesJailed)}
          sub={`${formatDecimal(perGame(stats.timesJailed))} per game`}
          icon="fa-lock"
          accent="#F87171"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-2.5">
        <Panel
          title="Property Empire"
          subtitle="Everything bought and built"
          icon="fa-city"
          accent="#FBBF24"
        >
          <div className="flex flex-col">
            <StatRow
              label="Properties bought"
              value={formatNumber(stats.propertiesBought)}
              color="#FBBF24"
            />
            <StatRow
              label="Houses built"
              value={formatNumber(stats.housesBuilt)}
              color="#34D399"
            />
            <StatRow
              label="Houses sold / lost"
              value={formatNumber(stats.housesDestroyed)}
              color="#F87171"
            />
            <StatRow
              label="Properties mortgaged"
              value={formatNumber(stats.propertiesMortgaged)}
              color="#94A3B8"
            />
            <StatRow
              label="Mortgages cleared"
              value={formatNumber(stats.propertiesUnmortgaged)}
              color="#38BDF8"
            />
            <StatRow
              label="Spent building"
              value={formatMoney(stats.totalSpentOnHouses)}
              color="#FB923C"
            />
          </div>
        </Panel>

        <Panel
          title="Auction House"
          subtitle="Bidding record"
          icon="fa-gavel"
          accent="#A78BFA"
        >
          <div className="mb-4 mobile:mb-[10px]">
            <StatBar
              label="Auction win rate"
              value={formatPercent(stats.derived.auctionWinRate, 0)}
              fraction={stats.derived.auctionWinRate / 100}
              color="#A78BFA"
              hint={`${formatNumber(stats.auctionsWon)} won of ${formatNumber(
                stats.auctionsBidIn
              )} bid in`}
            />
          </div>
          <div className="flex flex-col">
            <StatRow
              label="Auctions bid in"
              value={formatNumber(stats.auctionsBidIn)}
            />
            <StatRow
              label="Auctions won"
              value={formatNumber(stats.auctionsWon)}
              color="#A78BFA"
            />
            <StatRow
              label="Spent bidding"
              value={formatMoney(stats.moneySpentInAuctions)}
              color="#FB923C"
            />
            <StatRow
              label="Average winning bid"
              value={
                stats.auctionsWon > 0
                  ? formatMoney(stats.moneySpentInAuctions / stats.auctionsWon)
                  : "—"
              }
            />
          </div>
        </Panel>

        <Panel
          title="Negotiating Table"
          subtitle="Trades offered and settled"
          icon="fa-right-left"
          accent="#38BDF8"
        >
          <div className="mb-4 mobile:mb-[10px]">
            <StatBar
              label="Offers you accepted"
              value={formatPercent(stats.derived.tradeAcceptRate, 0)}
              fraction={stats.derived.tradeAcceptRate / 100}
              color="#38BDF8"
              hint={`${formatNumber(tradesResolved)} offers resolved`}
            />
          </div>
          <div className="flex flex-col">
            <StatRow
              label="Trades offered"
              value={formatNumber(stats.tradesOffered)}
            />
            <StatRow
              label="Trades accepted"
              value={formatNumber(stats.tradesAccepted)}
              color="#34D399"
            />
            <StatRow
              label="Trades declined"
              value={formatNumber(stats.tradesDeclined)}
              color="#F87171"
            />
            <StatRow
              label="Cash given away"
              value={formatMoney(stats.moneyGivenInTrades)}
              color="#F87171"
            />
            <StatRow
              label="Cash received"
              value={formatMoney(stats.moneyTakenInTrades)}
              color="#34D399"
            />
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-2.5">
        <Panel
          title="Dice & Movement"
          subtitle="Every roll on record"
          icon="fa-dice-five"
          accent="#FBBF24"
        >
          <div className="mb-4 mobile:mb-[10px]">
            <StatBar
              label="Doubles rate"
              value={formatPercent(stats.derived.doublesRate, 1)}
              fraction={stats.derived.doublesRate / 100}
              color="#FBBF24"
              hint="A fair pair of dice lands on 16.7%"
            />
          </div>
          <div className="flex flex-col">
            <StatRow
              label="Dice rolled"
              value={formatNumber(stats.totalDiceRolls)}
            />
            <StatRow
              label="Doubles rolled"
              value={formatNumber(stats.totalDoublesRolled)}
              color="#FBBF24"
            />
            <StatRow
              label="Tiles travelled"
              value={formatNumber(stats.totalTilesMoved)}
              color="#38BDF8"
            />
            <StatRow
              label="Laps of the board"
              value={formatNumber(stats.totalRoundsCompleted)}
              color="#34D399"
            />
            <StatRow
              label="Times jailed"
              value={formatNumber(stats.timesJailed)}
              color="#F87171"
            />
          </div>
        </Panel>

        <Panel
          title="Cards"
          subtitle="Chance and community chest"
          icon="fa-clone"
          accent="#FB923C"
        >
          <div className="flex flex-col">
            <StatRow label="Cards drawn" value={formatNumber(stats.cardsDrawn)} />
            <StatRow
              label="Action cards kept"
              value={formatNumber(stats.cardsKept)}
              color="#FBBF24"
            />
            <StatRow
              label="Action cards played"
              value={formatNumber(stats.cardsPlayed)}
              color="#FB923C"
            />
            <StatRow
              label="Played per game"
              value={formatDecimal(perGame(stats.cardsPlayed))}
            />
            <StatRow
              label="Kept but unplayed"
              value={formatNumber(
                Math.max((stats.cardsKept || 0) - (stats.cardsPlayed || 0), 0)
              )}
              color="#94A3B8"
            />
          </div>
        </Panel>

        <Panel
          title="Stock Exchange"
          subtitle="Capital in, capital out"
          icon="fa-chart-line"
          accent="#38BDF8"
        >
          <div className="flex flex-col">
            <StatRow
              label="Invested"
              value={formatMoney(stats.stockInvested)}
              color="#38BDF8"
            />
            <StatRow
              label="Retrieved"
              value={formatMoney(stats.stockRetrieved)}
              color="#34D399"
            />
            <StatRow
              label="Net profit"
              value={formatMoney(stats.derived.stockProfit)}
              color={stats.derived.stockProfit >= 0 ? "#34D399" : "#F87171"}
            />
            <StatRow
              label="Return"
              value={
                stats.stockInvested > 0
                  ? formatPercent(
                      (stats.derived.stockProfit / stats.stockInvested) * 100,
                      0
                    )
                  : "—"
              }
              color={stats.derived.stockProfit >= 0 ? "#34D399" : "#F87171"}
            />
            <StatRow
              label="Invested per game"
              value={formatMoney(perGame(stats.stockInvested))}
            />
          </div>
        </Panel>
      </div>
    </div>
  )
}

export default BoardTab
